<template>
  <div class="output-card">
    <div class="output-card__head">
      <div class="output-card__name">
        {{ title }}
      </div>
      <!-- No in-flight flag needed: output statuses come from
           encodingOutputJob.statusDescription, which draws on the same vocabulary
           as jobs/statuses (that response includes the output-level "Not
           Applicable" and "Packing"), so the map resolves them directly. -->
      <job-status-badge
        :status="output.status"
        size="sm"
      />
    </div>

    <div
      v-if="showProgress"
      class="output-card__progress"
    >
      <div class="output-card__progress-track">
        <div
          class="output-card__progress-fill"
          :style="{ width: `${progress}%` }"
        />
      </div>
      <span class="output-card__progress-value">{{ progress }}%</span>
    </div>

    <dl class="output-card__specs">
      <div class="output-card__spec">
        <dt>Video</dt>
        <dd>{{ output.video || '-' }}</dd>
      </div>
      <div class="output-card__spec">
        <dt>Audio</dt>
        <dd>{{ output.audio || '-' }}</dd>
      </div>
    </dl>

    <div
      v-if="output.path"
      class="output-card__path"
    >
      <a
        v-clipboard:copy="output.path"
        v-clipboard:success="copySuccess"
        class="output-card__path-link"
      >
        <span class="output-card__path-text">{{ output.path }}</span>
        <GjIcon
          name="Copy"
          size="18"
        />
      </a>
    </div>

    <div class="output-card__actions">
      <b-button
        variant="bg-white"
        size="sm"
        class="output-card__action"
        @click="$emit('show-logs', output)"
      >
        <GjIcon
          name="FileAlt"
          class="icon-left"
          size="16"
        />
        Logs
      </b-button>
      <b-button
        variant="bg-white"
        size="sm"
        class="output-card__action"
        @click="$emit('restart', output)"
      >
        <GjIcon
          name="Refresh"
          class="icon-left"
          size="16"
        />
        Restart
      </b-button>
      <b-button
        v-if="showTranscriptions"
        variant="bg-white"
        size="sm"
        class="output-card__action"
        :disabled="!transcriptionsReady"
        @click="transcriptionsOpen = !transcriptionsOpen"
      >
        {{ transcriptionsLabel }}
      </b-button>
    </div>

    <b-collapse
      v-if="showTranscriptions && transcriptionsReady"
      v-model="transcriptionsOpen"
    >
      <div class="output-card__transcriptions">
        <job-transcriptions
          :table-data="output.transcribeJob"
          :endpoint="endpoint"
          :destination="destination"
          :group="group"
          @closeTable="transcriptionsOpen = false"
        />
      </div>
    </b-collapse>
  </div>
</template>

<script>
import JobStatusBadge from './JobStatusBadge.vue'
import JobTranscriptions from '../summary-helpers/JobTranscriptions.vue'

const TRANSCRIBE_STATUS = {
  1: 'Pending',
  2: 'Processing',
  3: 'Finished',
}

export default {
  name: 'OutputMobileCard',
  components: {
    JobStatusBadge,
    JobTranscriptions,
  },
  props: {
    output: {
      type: Object,
      required: true,
    },
    isHls: {
      type: Boolean,
      default: false,
    },
    showTranscriptions: {
      type: Boolean,
      default: false,
    },
    endpoint: {
      type: String,
      default: null,
    },
    destination: {
      type: String,
      default: null,
    },
    group: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      transcriptionsOpen: false,
    }
  },
  computed: {
    title() {
      if (this.isHls) {
        return `${this.output.name} - ${this.output.nameModifier}`
      }
      return this.output.nameModifier || this.output.name
    },
    progress() {
      return this.output.progress
    },
    showProgress() {
      const notApplicable = (this.output.status || '')
        .toLowerCase()
        .includes('not applicable')
      return typeof this.progress === 'number' && !notApplicable
    },
    transcriptionsReady() {
      return this.output.transcribeJob?.status === 3
    },
    transcriptionsLabel() {
      const { transcribeJob } = this.output
      if (!transcribeJob) return 'Transcriptions failed'
      if (transcribeJob.status === 3) {
        return this.transcriptionsOpen
          ? 'Hide transcriptions'
          : 'Transcriptions'
      }
      return TRANSCRIBE_STATUS[transcribeJob.status] || 'Transcriptions failed'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.output-card {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid $gray-1234;
  border-radius: 8px;
  background-color: $white;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: $blue-700;
    overflow-wrap: anywhere;
  }

  &__progress {
    display: flex;
    align-items: center;
    margin-top: 10px;
  }

  &__progress-track {
    flex: 1 1 auto;
    height: 6px;
    border-radius: 3px;
    background-color: $gray-100;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    border-radius: 3px;
    background-color: $primary;
    transition: width 0.3s ease;
  }

  &__progress-value {
    flex: 0 0 auto;
    margin-left: 8px;
    font-size: 12px;
    font-weight: 600;
    color: $blue-700;
  }

  &__specs {
    display: flex;
    margin: 12px 0 0;
  }

  &__spec {
    flex: 1 1 0;
    min-width: 0;

    dt {
      margin: 0;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: $blue-800;
    }

    dd {
      margin: 2px 0 0;
      font-size: 13px;
      color: $blue-700;
      overflow-wrap: anywhere;
    }
  }

  &__path {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid $gray-1234;
  }

  &__path-link {
    display: flex;
    align-items: center;
    gap: 6px;
    color: $primary;
  }

  &__path-text {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    direction: ltr;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  &__action {
    margin-right: 0 !important;
  }

  &__transcriptions {
    margin-top: 12px;
  }
}
</style>
